import { SectionTitle, SectionTitleSizes } from '@components/common/section-title';
import { Head } from '@components/common/head';
import { useI18NextContext } from '@components/hooks';
import { ComponentContainer, SectionSeparator } from '@styles/layout';
import { Theme } from '@theme/base';
import React, { useEffect } from 'react';
import { CMSDestination, CMSTravelGuides, Entry, TravelGuide, TripTypes } from '@model/contentful';
import { Grid } from '@mui/material';
import { PageProps } from '@model/pages';
import { Place } from '@model/iceberg/geography';
import { GuideLink } from '@components/common/next-link';
import { GuidesBreadcrumb } from '@components/breadcrumbs';
import { useDispatch, useSelector } from 'react-redux';
import { Footer } from '@components/footer';
import { DestinationHero } from '@components/common/hero/destination/DestinationHero';
import { Error404 } from '@components/common/hero/error/Error404';
import { Error500 } from '@components/common/hero/error/Error500';
import { useAsync } from 'react-use';
import { getHero } from '@components/util';
import { Page } from '@model/common';
import { setTripTypeFilter } from '@state/navigation';
import { getCMSDestination } from '@state/cms';
import { SimpleTile } from '@components/common/simple-tile';
import { ERROR_CODES } from '@model/common/error-codes';

/* ***************** *
 *       Types       *
 * ***************** */
export interface TravelGuidesListPageLayoutProps extends PageProps {
  theme: Theme;
  cmsConfig: any;
  place: Place;
  path: string;
  tripType?: TripTypes;
}

export const TravelGuidesListPageLayout = ({
  cmsConfig,
  theme,
  path,
  tripType,
  promoBannerTripType
}: TravelGuidesListPageLayoutProps) => {
  const t: any = useI18NextContext();
  const destination: CMSDestination | undefined = useSelector(getCMSDestination(path));
  const destinationPath: string = destination?.destinationPath?.fields?.path || '';
  const destinationTitle: string = destination?.title || '';
  const cmsTravelGuides: CMSTravelGuides =
    !!cmsConfig.travelGuides && !!cmsConfig.travelGuides.length && cmsConfig.travelGuides;

  if (!cmsConfig) return <Error500 code={ERROR_CODES.NO_CMS_CONFIG} />;
  if (!cmsTravelGuides || !cmsTravelGuides.length) return <Error404 />;

  const { value: hero }: any = useAsync(async () => {
    return await getHero({ path });
  }, []);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setTripTypeFilter(tripType || TripTypes.ALL));
  }, []);

  /* *** RENDERERS *** */
  const renderTravelGuideTiles = () =>
    cmsTravelGuides.map((travelGuide: Entry<TravelGuide>) => {
      const {
        fields: { title, shortDescription, heroImage, path: travelGuidePath }
      } = travelGuide;

      return (
        <Grid item xs={12} sm={6} lg={4} key={travelGuidePath}>
          <GuideLink path={path} guidePath={travelGuidePath} tripType={tripType} guidePage={Page.TRAVEL_GUIDES}>
            <SimpleTile title={title} shortDescription={shortDescription} imageUrl={heroImage.fields.file.url} />
          </GuideLink>
        </Grid>
      );
    });
  const metaTitle: string = t('seo__travel-guide-listing--title', { destination: destinationTitle });
  const metaDescription: string = t('seo__travel-guide-listing--description', {
    destination: destinationTitle
  });

  return (
    <>
      <Head metaTitle={metaTitle} metaDescription={metaDescription} />
      <DestinationHero src={hero?.images[0]} promoBannerTripType={promoBannerTripType} />
      <GuidesBreadcrumb
        destination={{
          path: destinationPath,
          display: destinationTitle
        }}
        tripType={tripType}
        guidePage={Page.TRAVEL_GUIDES}
      />
      <ComponentContainer>
        <SectionSeparator />
        <SectionTitle
          size={SectionTitleSizes.LARGE}
          main
          heading={{
            label: 'travel-guides__page-title--label',
            vars: { destination: cmsConfig.destination.fields.title }
          }}
        />
        <Grid container spacing={theme!.custom.gridSpacing.default}>
          {renderTravelGuideTiles()}
        </Grid>
      </ComponentContainer>
      <SectionSeparator />
      <Footer />
    </>
  );
};
